import { HTMLInputTypeAttribute, ReactNode, useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import IconEye from "../../../components/icons/icon-eye";
import IconLockDots from "../../../components/icons/icon-lock-dots";
import { useFetchData } from "../../../hooks/useFetch";
import { MODULE_ROUTE } from "../../../routers/module-router";
import { API_OAUTH_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";

export const ChangePasswordPage = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [reNewPassword, setReNewPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showReNewPassword, setShowReNewPassword] = useState(false);

  const [showErrorOldPassword, setShowErrorOldPassword] = useState(false);
  const [showErrorNewPassword, setShowErrorNewPassword] = useState(false);
  const [showErrorReNewPassword, setShowErrorReNewPassword] = useState(false);

  const [errorOldPassword, setErrorOldPassword] = useState("");
  const [errorNewPassword, setErrorNewPassword] = useState("");
  const [errorReNewPassword, setErrorReNewPassword] = useState("");

  const { request: onChangePassword, baseResponse: responseChangePassword, loading: isLoading } = useFetchData.Post();

  useEffect(() => {
    if (!responseChangePassword) {
      return;
    }
    if (responseChangePassword.status === 200) {
      showToast({ message: "Đổi mật khẩu mới thành công", type: "success" });
      CookieUtils.eraseCookie("user");
      setTimeout(() => {
        window.location.href = MODULE_ROUTE.LOGIN;
      }, 1000);
    } else {
      showToast({ message: responseChangePassword.message, type: "warning" });
    }
  }, [responseChangePassword]);

  const resetErrors = () => {
    setShowErrorOldPassword(false);
    setShowErrorNewPassword(false);
    setShowErrorReNewPassword(false);
    setErrorOldPassword("");
    setErrorNewPassword("");
    setErrorReNewPassword("");
  };

  const handleChangePassword = () => {
    resetErrors();
    if (!validateNewPassword()) {
      return;
    }
    onChangePassword({
      endPoint: API_OAUTH_ROUTER.CHANGE_PASSWORD,
      data: {
        old_password: btoa(oldPassword),
        new_password: btoa(newPassword),
      },
    });
  };

  const validateNewPassword = () => {
    let isValid = true;
    if (oldPassword.length < 6 || oldPassword.length > 20) {
      setShowErrorOldPassword(true);
      setErrorOldPassword("Mật khẩu phải từ 6 đến 20 kí tự");
      isValid = false;
    }

    if (newPassword.length < 6 || newPassword.length > 20) {
      setShowErrorNewPassword(true);
      setErrorNewPassword("Mật khẩu phải từ 6 đến 20 kí tự");
      isValid = false;
    }

    if (newPassword === oldPassword) {
      setShowErrorNewPassword(true);
      setErrorNewPassword("Mật khẩu mới không được giống mật khẩu cũ");
      isValid = false;
    }

    if (reNewPassword.length < 6 || reNewPassword.length > 20) {
      setShowErrorReNewPassword(true);
      setErrorReNewPassword("Mật khẩu phải từ 6 đến 20 kí tự");
      isValid = false;
    }

    if (newPassword !== reNewPassword) {
      setShowErrorReNewPassword(true);
      setErrorReNewPassword("Mật khẩu nhập lại không giống nhau");
      isValid = false;
    }

    return isValid;
  };

  interface RenderComponentProps {
    labelText: string;
    icon?: ReactNode;
    type?: HTMLInputTypeAttribute;
    value: string;
    setValue: (value: string) => void;
    isShowError?: boolean;
    messageError?: string;
    showPassword: boolean;
    setShowPassword: (value: boolean) => void;
  }

  function renderComponent({
    labelText,
    type,
    icon,
    value,
    setValue,
    showPassword,
    setShowPassword,
    isShowError,
    messageError,
  }: RenderComponentProps) {
    return (
      <div>
        <label htmlFor={labelText} className="block mb-2 text-sm font-medium text-gray-900">
          {labelText}
        </label>
        <div className="relative">
          <span className="absolute -translate-y-1/2 start-4 top-1/2">{icon}</span>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleChangePassword();
              }
            }}
            type={type}
            placeholder="Nhập thông tin"
            aria-invalid={isShowError}
            aria-describedby={isShowError ? `${labelText}-error` : undefined}
            className={`${
              isShowError ? "border-red-500 " : "border-gray-300 "
            }ps-10 bg-gray-50 border  text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
          />
          <button
            type="button"
            className="absolute -translate-y-1/2 end-4 top-1/2"
            data-testid="toggle-password-visibility"
            onClick={() => setShowPassword(!showPassword)}
          >
            <IconEye isIconOff={showPassword} />
          </button>
        </div>
        {isShowError && (
          <span id={`${labelText}-error`} className="text-sm text-red-500">
            {messageError}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center" style={{ height: `calc(100vh - ${56 + 24 + 24}px)` }}>
      <div className="w-full p-6 bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md dark:bg-gray-800 dark:border-gray-700 sm:p-8">
        <h2 className="mb-1 text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
          Đổi mật khẩu
        </h2>
        <form
          className="mt-4 space-y-4 lg:mt-5 md:space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            handleChangePassword();
          }}
          data-testid="form-change-password"
        >
          {renderComponent({
            labelText: "Mật khẩu hiện tại",
            type: showOldPassword ? "text" : "password",
            value: oldPassword,
            setValue: setOldPassword,
            showPassword: showOldPassword,
            setShowPassword: setShowOldPassword,
            isShowError: showErrorOldPassword,
            messageError: errorOldPassword,
            icon: <IconLockDots fill={false} data-testid="old-password-icon" />,
          })}
          {renderComponent({
            labelText: "Mật khẩu mới",
            type: showNewPassword ? "text" : "password",
            value: newPassword,
            setValue: setNewPassword,
            showPassword: showNewPassword,
            setShowPassword: setShowNewPassword,
            isShowError: showErrorNewPassword,
            messageError: errorNewPassword,
            icon: <IconLockDots fill={false} data-testid="new-password-icon" />,
          })}
          {renderComponent({
            labelText: "Nhập lại mật khẩu",
            type: showReNewPassword ? "text" : "password",
            value: reNewPassword,
            setValue: setReNewPassword,
            showPassword: showReNewPassword,
            setShowPassword: setShowReNewPassword,
            isShowError: showErrorReNewPassword,
            messageError: errorReNewPassword,
            icon: <IconLockDots fill={false} data-testid="re-new-password-icon" />,
          })}

          <button
            type="button"
            data-testid="button-change-password"
            className="btn btn-gradient w-full border-0 shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
            disabled={isLoading}
            onClick={handleChangePassword}
          >
            {isLoading && (
              <span className="inline-block w-5 h-5 align-middle border-2 border-white rounded-full animate-spin border-l-transparent ltr:mr-4 rtl:ml-4"></span>
            )}
            Đặt lại mật khẩu
          </button>
        </form>
      </div>
    </div>
  );
};
